// ---- Mesh kit ----
// Shared low-level mesh primitives used to author procedural models. Both the
// scatter `deco` system and the walkable `structure` system build their visuals
// from these, so there's a single source of truth for how a "box" / "cone" /
// "blob" looks (geometry, toon material, faceting). Each primitive builds a mesh
// and hands it to the caller's `push` so the caller controls grouping/shadows.

import * as THREE from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry.js";
import { rand } from "./color.ts";
import { toonMat, toonMatSmooth, flatten } from "./toon.ts";

// Unit rounded box, scaled per call — props, furniture, soft architectural
// accents (window frames, stoops). Exported so structure consolidation can
// recognize box prims by identity and instance them instead of merging.
export const UNIT_BOX = new RoundedBoxGeometry(1, 1, 1, 3, 0.12);

// Softer unit box — the CHARACTER prim: more segments + a fatter corner radius
// so figures read as little stuffed characters rather than stacks of hard
// cubes. Shared by the player rig (player.ts) and every NPC/creature build
// (npc/define.ts npcMeshGroup passes it to makePrims), so the player and NPCs
// are literally the same prim — props/buildings keep the crisper UNIT_BOX.
export const SOFT_BOX = new RoundedBoxGeometry(1, 1, 1, 4, 0.16);

// SHARP unit box — THE building-shell prim. Walls/floors/ceilings of a mass
// must NOT use rounded corners: two adjacent RoundedBoxes leave soft notches
// at every join that read as "separate wall panels". Sharp boxes coplanar-
// meet as one continuous plane. Structure wall/floor/ceiling use this.
export const UNIT_SHARP = new THREE.BoxGeometry(1, 1, 1);

// Shared unit cylinder (r=1, h=1 along +Y, 8 radial segs) — architecture soft
// trim (posts, tubes, rods, ridge beams). Pre-flattened (faceted normals) so
// chunky toon banding matches the historic flatten() path while still sharing
// one geometry for structure InstancedMesh consolidation.
export const UNIT_CYL = (() => {
  const raw = new THREE.CylinderGeometry(1, 1, 1, 8);
  const g = flatten(raw);
  if (g !== raw) raw.dispose();
  return g;
})();

// Shared unit sphere — ridge end-caps, soft eave tips, quoin balls, and
// character balls (via ball()). 16×12 matches the historic per-call sphere
// quality so flesh/trim silhouettes stay BC while consolidating for structures.
export const UNIT_SPHERE = new THREE.SphereGeometry(1, 16, 12);

/** Process-shared geometries that must NEVER be disposed (every box/cyl/sphere
 *  prim scales one of these). disposeGroup + structure consolidate key on
 *  identity. */
export const SHARED_GEOS: ReadonlySet<THREE.BufferGeometry> = new Set([
  UNIT_BOX, SOFT_BOX, UNIT_SHARP, UNIT_CYL, UNIT_SPHERE,
]);

/** Tear down a built group's GPU resources (map swap, plan 24 — the studio's
 *  worldstage has its own twin): dispose every geometry it owns — skipping the
 *  process-shared unit geos above, which every box/cyl/sphere prim scales — and
 *  release InstancedMesh buffers. Materials are NOT disposed unless tagged
 *  `userData.owned = true` at creation (the per-build water/structure
 *  materials); everything else draws from the process-wide toonMat cache. */
export function disposeGroup(root: THREE.Object3D): void {
  root.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh) return;
    const im = m as unknown as THREE.InstancedMesh;
    if (im.isInstancedMesh) im.dispose();
    if (!SHARED_GEOS.has(m.geometry)) m.geometry.dispose();
    const mat = m.material as THREE.Material | THREE.Material[];
    for (const one of Array.isArray(mat) ? mat : [mat]) {
      if (!one?.userData?.owned) continue;
      // Free owned textures first (shop-sign CanvasTexture, etc.). dispose() on
      // the material alone does not release GPU uploads for map slots.
      const maps = [
        (one as THREE.MeshBasicMaterial).map,
        (one as THREE.MeshToonMaterial).map,
        (one as THREE.MeshStandardMaterial).map,
        (one as THREE.MeshStandardMaterial).normalMap,
        (one as THREE.MeshStandardMaterial).emissiveMap,
      ];
      for (const tex of maps) {
        if (tex && (tex.userData?.owned || one.userData?.ownedMaps)) tex.dispose();
      }
      one.dispose();
    }
  });
}

/** Optional Euler XYZ (radians) applied after scale — lets JSON parts lean a box/
 *  cylinder off-axis (bow limbs, diagonal beams). Omitted = axis-aligned rest pose. */
export type PrimRot = readonly [number, number, number];

export type Prims = {
  /** Flat-shaded rounded box. x/y/z = center, w/h/d = size. y grows UP. */
  box: (x: number, y: number, z: number, w: number, h: number, d: number, color: number, rot?: PrimRot) => void;
  /** Organic lump (displaced icosahedron) — for rocks/boulders. x/y/z = center. */
  blob: (x: number, y: number, z: number, radius: number, color: number, rot?: PrimRot) => void;
  /** Smooth sphere — a clean ball of flesh (deltoid, hand, joint, hair knot).
   *  Unlike `blob` (a chunky displaced boulder), this is smooth-shaded to match
   *  the `bone`/`lathe` capsule character look. `depth` ovalizes along local z
   *  (a flatter hand / squashed cheek); `rot` tilts it. x/y/z = center. */
  ball: (x: number, y: number, z: number, radius: number, color: number, depth?: number, rot?: PrimRot) => void;
  /** Smooth arc — a partial torus lying in the XY plane, curving UP at the ends
   *  (a smile / bow) by default. `radius` = the arc's bend radius, `tube` = its
   *  thickness, `sweep` = how much of the circle it spans (rad). x/y/z anchors
   *  the arc's LOW point (the center dip); the ends rise from there. `rot` spins
   *  it — rot.z = π flips it to a frown, a half-π stands it vertical. One clean
   *  smooth-shaded mesh, so it reads as ONE continuous curve (mouths, brows). */
  arc: (x: number, y: number, z: number, radius: number, tube: number, color: number, sweep: number, rot?: PrimRot) => void;
  /** Low-poly cone. x/z = center, baseY = bottom. */
  cone: (x: number, baseY: number, z: number, radius: number, height: number, color: number, rot?: PrimRot) => void;
  /** Low-poly cylinder. x/z = center, baseY = bottom. */
  cylinder: (x: number, baseY: number, z: number, rTop: number, rBottom: number, height: number, color: number, rot?: PrimRot) => void;
  /** Horizontal cylinder (a log). x/y/z = center, laid along the given axis. */
  tube: (x: number, y: number, z: number, radius: number, length: number, color: number, axis?: "x" | "z") => void;
  /** Cylinder between two world points (any orientation) — bow limbs, staves, rails.
   *  Prefer this over hand-computed box `rot` for path-like geometry. */
  rod: (x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, radius: number, color: number) => void;
  /** Tapered capsule between two world points — THE limb-segment prim (plan 27).
   *  Hemispherical caps sit centered ON the endpoints (a ball of flesh around each
   *  joint), so the mesh extends r0/r1 past them along the axis. r0 = radius at
   *  the (x0,y0,z0) end, r1 at the far end — unequal radii taper the flank.
   *  `depth` (default 1) scales the cross-section along local z, turning the
   *  circular section oval — torsos/heads are wider than deep.
   *  `bulge` swells the flank into a muscle belly: `{ amount, at? }` where
   *  amount is a fractional radius gain at the belly peak and `at` (0=r0 end,
   *  1=r1 end, default 0.5) positions it (bicep ≈ 0.45, calf ≈ 0.3). Omitted =
   *  a straight taper. */
  bone: (x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, r0: number, r1: number, color: number, depth?: number, bulge?: { amount: number; at?: number }) => void;
  /** CUSTOM rounded solid: spin a silhouette profile around a vertical axis at
   *  (x,z) — the escape hatch for organic masses a capsule can't express (a
   *  torso with a waist pinch, an egg head, a hair dome). `profile` is
   *  [radius, y] pairs bottom→top in model units (y relative to `baseY`);
   *  radius 0 closes a pole. `depth` ovalizes the section (z-scale); `segs` =
   *  radial facets (default 8 — keep low for the toon look). `rot` tilts the
   *  whole shape about (x,baseY,z) — profile ys should be LOCAL to baseY when
   *  tilting, or the lever arm displaces the shape. `frontBulge` = [y, dz] pairs
   *  (profile y-space) that push ONLY the front (+z) surface forward per height —
   *  a front-only chest/belly on the one mass, sides + back untouched. */
  lathe: (x: number, baseY: number, z: number, profile: readonly (readonly [number, number])[], color: number, depth?: number, segs?: number, rot?: PrimRot, frontBulge?: readonly (readonly [number, number])[]) => void;
  /** Emit the prims created inside `fn` into a named pivot sub-group anchored at
   *  `pivot` (feet-space). The prims are auto-offset by −pivot so their WORLD
   *  position at rest is unchanged (a model that never calls `limb` is
   *  byte-identical to before, and the snapshot guard — which fingerprints world
   *  AABBs, and counts meshes not groups — is unaffected). Rotating the returned
   *  group animates its children about the pivot, so archetype legs/arms/wings can
   *  be rigged for a shared walk/run/attack (see src/model/gait.ts). Nestable.
   *  Returns the group (tagged `userData.limb = name`). */
  limb: (name: string, pivot: readonly [number, number, number], fn: () => void) => THREE.Group;
  /** An EMPTY named attachment point (tagged `userData.socket = name`) at
   *  `pivot` — no geometry, so it's invisible to the snapshot guard (which
   *  counts meshes, not groups). Nest it inside a limb() to make the socket
   *  ride that joint (e.g. a handMain socket inside the elbow pivot, so an
   *  equipped weapon inherits the arm's swing — see src/model/rig.ts). */
  socket: (name: string, pivot: readonly [number, number, number]) => THREE.Group;
  /** RE-ENTER a limb group already created by `limb()` and emit the prims from
   *  `fn` inside it (feet-space coords, offset handled) — how slot fillers
   *  (content/parts) land inside a rig joint so a helmet nods with the head.
   *  Falls back to root emission when no such limb exists (unjointed builds:
   *  correct rest position, just static). */
  inLimb: (name: string, fn: () => void) => void;
};

/**
 * Build the primitive set bound to a `push` sink and a per-instance `seed`.
 * The seed salts the blob displacement so duplicated models vary deterministically.
 * `boxGeo` picks the unit box the `box` prim scales — UNIT_BOX (default, props/
 * buildings) or SOFT_BOX (characters).
 */
/** Build a tapered-capsule geometry along +Y: a hemispherical cap of radius r0
 *  at the bottom, a straight tapered flank, a cap of radius r1 at the top.
 *  `len` is the joint-to-joint distance (cap CENTERS), so the mesh spans
 *  −len/2−r0 … +len/2+r1 after the recenter. Radial segs kept coarse; normals
 *  stay SMOOTH (LatheGeometry's own) — the SOFT_BOX character look. Flattening
 *  them facets every quad ("disco ball"). Three's CapsuleGeometry is
 *  uniform-radius only, hence the lathe. */
function taperedCapsuleGeo(
  r0: number, r1: number, len: number,
  bulge = 0, bulgeAt = 0.5,
): THREE.BufferGeometry {
  const CAP_SEGS = 3;
  const pts: THREE.Vector2[] = [];
  for (let i = 0; i <= CAP_SEGS; i++) {
    const a = -Math.PI / 2 + (Math.PI / 2) * (i / CAP_SEGS); // bottom pole → equator
    pts.push(new THREE.Vector2(Math.cos(a) * r0, Math.sin(a) * r0));
  }
  // Flank: r0 (bottom equator) → r1 (top equator). A `bulge` swells the middle
  // into a muscle belly — a smooth sine hump that's 0 at both equators (so the
  // caps still seat cleanly) and peaks (× base radius) at `bulgeAt` (0=r0 end,
  // 1=r1 end). amount 0 = a single straight segment, byte-identical to before.
  if (bulge > 0) {
    const FLANK_SEGS = 8;
    for (let i = 1; i < FLANK_SEGS; i++) {
      const t = i / FLANK_SEGS;
      const hump = t <= bulgeAt
        ? Math.sin((Math.PI / 2) * (t / bulgeAt))
        : Math.sin((Math.PI / 2) * ((1 - t) / (1 - bulgeAt)));
      const base = r0 + (r1 - r0) * t;
      pts.push(new THREE.Vector2(base * (1 + bulge * hump), len * t));
    }
  }
  for (let i = 1; i <= CAP_SEGS; i++) {
    const a = (Math.PI / 2) * (i / CAP_SEGS); // equator → top pole
    pts.push(new THREE.Vector2(Math.cos(a) * r1, len + Math.sin(a) * r1));
  }
  const g = new THREE.LatheGeometry(pts, 8);
  g.translate(0, -len / 2, 0); // center on the joint-to-joint midpoint
  return g;
}

/** Bulge the FRONT (+z) surface of a lathe forward, per height, leaving the
 *  SIDES and BACK untouched — so a body-of-revolution mass (a torso) can grow a
 *  front-only chest/belly WITHOUT a separate prim. `bulge` = [y, dz] pairs in
 *  the profile's own y-space (interpolated, clamped to the ends). Each vertex is
 *  pushed +z by dz(y) weighted by how forward it faces (its `z/r` = cos of the
 *  revolve angle): full at dead-front, easing to 0 at the sides, and never on
 *  the back (weight clamped ≥ 0). Mutates + re-smooths the geometry in place. */
function pushLatheFront(geo: THREE.BufferGeometry, bulge: readonly (readonly [number, number])[]): void {
  const first = bulge[0]!, last = bulge[bulge.length - 1]!;
  const dzAt = (y: number): number => {
    if (y <= first[0]) return first[1];
    if (y >= last[0]) return last[1];
    for (let i = 1; i < bulge.length; i++) {
      const [y1, d1] = bulge[i]!;
      if (y <= y1) {
        const [y0, d0] = bulge[i - 1]!;
        return d0 + (d1 - d0) * ((y - y0) / (y1 - y0 || 1));
      }
    }
    return last[1];
  };
  const pos = geo.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    const vx = pos.getX(i), vy = pos.getY(i), vz = pos.getZ(i);
    const r = Math.hypot(vx, vz);
    if (r < 1e-6) continue;
    const forward = vz / r; // +1 dead-front, 0 at the sides, −1 at the back
    if (forward <= 0) continue;
    const dz = dzAt(vy) * forward;
    if (dz !== 0) pos.setZ(i, vz + dz);
  }
  pos.needsUpdate = true;
  geo.computeVertexNormals(); // re-smooth so lighting follows the new front
}

export function makePrims(push: (mesh: THREE.Mesh) => void, seed: number, boxGeo: THREE.BufferGeometry = UNIT_BOX): Prims {
  // Current limb group (null = emit into the root via `push`) + the active pivot
  // offset. Inside limb() we still route meshes through `push` for its decoration
  // (shadow flags), then re-parent them into the limb group — THREE.add() detaches
  // the mesh from the root first, so the net parent is the limb group.
  let container: THREE.Object3D | null = null;
  let ox = 0, oy = 0, oz = 0;
  // Every limb created, with its ABSOLUTE (feet-space) pivot — limb() receives
  // absolute pivots (it subtracts the parent offset itself), so inLimb can
  // re-enter any of them, nested or not.
  const limbReg = new Map<string, { group: THREE.Group; pivot: [number, number, number] }>();
  const place = (m: THREE.Mesh) => {
    if (!container) { push(m); return; }
    m.position.x -= ox; m.position.y -= oy; m.position.z -= oz; // → group-local
    push(m);            // decorate (castShadow/receiveShadow) + add to root
    container.add(m);    // re-parent into the limb group
  };
  const applyRot = (m: THREE.Mesh, rot?: PrimRot) => {
    if (rot) m.rotation.set(rot[0], rot[1], rot[2]);
  };
  return {
    box: (x, y, z, w, h, d, color, rot) => {
      const m = new THREE.Mesh(boxGeo, toonMat(color)); // smooth normals → soft banding
      m.position.set(x, y, z);
      m.scale.set(w, h, d);
      applyRot(m, rot);
      place(m);
    },
    blob: (x, y, z, radius, color, rot) => {
      const g = new THREE.IcosahedronGeometry(radius, 1); // subdiv 1 = chunky
      const p = g.attributes.position as THREE.BufferAttribute;
      const salt = x * 31.7 + z * 17.3 + radius * 41.3 + seed * 97;
      for (let i = 0; i < p.count; i++) {
        const px = p.getX(i), py = p.getY(i), pz = p.getZ(i);
        // Displace from the vertex POSITION (not index) so the non-indexed
        // icosahedron's duplicated corner vertices move identically and the
        // triangles don't tear apart. Gentle range keeps it lumpy.
        const f = 0.84 + rand(salt + px * 12.9898 + py * 78.233 + pz * 37.719) * 0.32;
        p.setXYZ(i, px * f, py * f * 0.78, pz * f); // squash vertically → wide, low boulders
      }
      g.computeVertexNormals();
      const m = new THREE.Mesh(g, toonMat(color));
      m.position.set(x, y, z);
      applyRot(m, rot);
      place(m);
    },
    ball: (x, y, z, radius, color, depth, rot) => {
      // Shared UNIT_SPHERE (scaled) so structure soft-caps consolidate via
      // InstancedMesh. Smooth ramp for character flesh; structures re-material
      // to chunky vertex-color at consolidate time.
      const m = new THREE.Mesh(UNIT_SPHERE, toonMatSmooth(color));
      m.scale.set(radius, radius, radius * (depth ?? 1));
      m.position.set(x, y, z);
      applyRot(m, rot);
      place(m);
    },
    arc: (x, y, z, radius, tube, color, sweep, rot) => {
      // Partial torus (TorusGeometry's `arc` runs from +X, CCW, by `sweep`).
      // Center that span on the BOTTOM (−Y) so the ends curve up = a smile,
      // then lift so the low point sits at local y=0 → `y` anchors the dip.
      const g = new THREE.TorusGeometry(radius, tube, 8, 20, sweep);
      g.rotateZ(-Math.PI / 2 - sweep / 2);
      g.translate(0, radius, 0);
      const m = new THREE.Mesh(g, toonMatSmooth(color)); // curved → smooth ramp
      m.position.set(x, y, z);
      applyRot(m, rot);
      place(m);
    },
    cone: (x, baseY, z, radius, height, color, rot) => {
      const m = new THREE.Mesh(flatten(new THREE.ConeGeometry(radius, height, 7)), toonMat(color));
      m.position.set(x, baseY + height / 2, z);
      applyRot(m, rot);
      place(m);
    },
    cylinder: (x, baseY, z, rTop, rBottom, height, color, rot) => {
      // Equal radii → shared UNIT_CYL (structure soft posts instance). Tapered
      // still allocates (rare on buildings; barrels/furniture).
      let m: THREE.Mesh;
      if (rTop === rBottom) {
        m = new THREE.Mesh(UNIT_CYL, toonMat(color));
        m.scale.set(rTop, height, rTop);
      } else {
        m = new THREE.Mesh(flatten(new THREE.CylinderGeometry(rTop, rBottom, height, 7)), toonMat(color));
      }
      m.position.set(x, baseY + height / 2, z);
      applyRot(m, rot);
      place(m);
    },
    tube: (x, y, z, radius, length, color, axis = "x") => {
      const m = new THREE.Mesh(UNIT_CYL, toonMat(color));
      m.scale.set(radius, length, radius);
      if (axis === "x") m.rotation.z = Math.PI / 2;
      else m.rotation.x = Math.PI / 2;
      m.position.set(x, y, z);
      place(m);
    },
    rod: (x0, y0, z0, x1, y1, z1, radius, color) => {
      const dx = x1 - x0, dy = y1 - y0, dz = z1 - z0;
      const len = Math.hypot(dx, dy, dz);
      if (len < 1e-8) return;
      const m = new THREE.Mesh(UNIT_CYL, toonMat(color));
      m.scale.set(radius, len, radius);
      m.position.set((x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2);
      // Default cylinder is +Y; spin so +Y aligns with (dx,dy,dz).
      const dir = new THREE.Vector3(dx / len, dy / len, dz / len);
      m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
      place(m);
    },
    bone: (x0, y0, z0, x1, y1, z1, r0, r1, color, depth, bulge) => {
      const dx = x1 - x0, dy = y1 - y0, dz = z1 - z0;
      const len = Math.hypot(dx, dy, dz);
      if (len < 1e-8) return;
      const m = new THREE.Mesh(taperedCapsuleGeo(r0, r1, len, bulge?.amount ?? 0, bulge?.at ?? 0.5), toonMatSmooth(color)); // curved → smooth ramp (no cel rings)
      if (depth !== undefined) m.scale.z = depth; // local-z: flattens the section
      m.position.set((x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2);
      m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), new THREE.Vector3(dx / len, dy / len, dz / len));
      place(m);
    },
    lathe: (x, baseY, z, profile, color, depth, segs, rot, frontBulge) => {
      const pts = profile.map(([r, y]) => new THREE.Vector2(r, y));
      // Smooth normals (LatheGeometry's own) — flattening facets the mass. Curved
      // surface → smooth ramp (TOON_SMOOTH) so the mass grades softly, no cel rings.
      const geo = new THREE.LatheGeometry(pts, segs ?? 8);
      if (frontBulge && frontBulge.length) pushLatheFront(geo, frontBulge); // front-only chest/belly
      const m = new THREE.Mesh(geo, toonMatSmooth(color));
      if (depth !== undefined) m.scale.z = depth;
      m.position.set(x, baseY, z);
      applyRot(m, rot);
      place(m);
    },
    limb: (name, pivot, fn) => {
      const [px, py, pz] = pivot;
      const g = new THREE.Group();
      g.name = name;
      g.userData.limb = name;
      // Position the group in its PARENT's local space (subtract the parent pivot
      // for nesting; parent is the root when ox/oy/oz are 0).
      g.position.set(px - ox, py - oy, pz - oz);
      const prevContainer = container, pox = ox, poy = oy, poz = oz;
      container = g; ox = px; oy = py; oz = pz;
      fn();
      container = prevContainer; ox = pox; oy = poy; oz = poz;
      if (prevContainer) prevContainer.add(g);
      else push(g as unknown as THREE.Mesh); // decorate (harmless on a group) + add to root
      limbReg.set(name, { group: g, pivot: [px, py, pz] });
      return g;
    },
    inLimb: (name, fn) => {
      const entry = limbReg.get(name);
      if (!entry) { fn(); return; } // unjointed build → root emission
      const prevContainer = container, pox = ox, poy = oy, poz = oz;
      container = entry.group;
      [ox, oy, oz] = entry.pivot;
      fn();
      container = prevContainer; ox = pox; oy = poy; oz = poz;
    },
    socket: (name, pivot) => {
      const [px, py, pz] = pivot;
      const g = new THREE.Group();
      g.name = name;
      g.userData.socket = name;
      // Same parent-local placement rule as limb(): subtract the active pivot.
      g.position.set(px - ox, py - oy, pz - oz);
      if (container) container.add(g);
      else push(g as unknown as THREE.Mesh);
      return g;
    },
  };
}
